<script lang="ts" setup>
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { ECBasicOption } from 'echarts/types/dist/shared';
import {
    PieChart,
    GaugeChart,
    BarChart,
    HeatmapChart,
    LineChart,
    RadarChart,
} from 'echarts/charts';
import {
    TitleComponent,
    TooltipComponent,
    LegendComponent,
    GridComponent,
    VisualMapComponent,
} from 'echarts/components';
import VChart, { THEME_KEY } from 'vue-echarts';
import { nextTick, onBeforeUnmount, onMounted, PropType, provide, ref } from 'vue';

use([
    CanvasRenderer,
    PieChart,
    BarChart,
    GaugeChart,
    HeatmapChart,
    LineChart,
    RadarChart,
    TitleComponent,
    TooltipComponent,
    LegendComponent,
    GridComponent,
    VisualMapComponent,
]);

provide(THEME_KEY, 'light');

const props = defineProps({
    option: {
        type: Object as PropType<ECBasicOption>,
    },
    autoresize: {
        type: Boolean,
        default: true,
    },
    height: {
        type: String,
        default: '320px',
    },
});

const emit = defineEmits<{
    mouseover: [params: unknown];
    mouseout: [params: unknown];
    click: [params: unknown];
}>();

const containerRef = ref<HTMLElement | null>(null);
const isReady = ref(false);
let resizeObserver: ResizeObserver | null = null;
let animationFrame: number | null = null;

const scheduleReadinessCheck = () => {
    if (animationFrame !== null) return;

    animationFrame = window.requestAnimationFrame(() => {
        animationFrame = null;
        const container = containerRef.value;
        if (!container) return;

        const isVisible = container.clientWidth > 0 && container.clientHeight > 0;
        if (!isVisible) return;

        isReady.value = true;
        resizeObserver?.disconnect();
        resizeObserver = null;
    });
};

onMounted(async () => {
    await nextTick();
    resizeObserver = new ResizeObserver(scheduleReadinessCheck);
    if (containerRef.value) resizeObserver.observe(containerRef.value);
    scheduleReadinessCheck();
});

onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
});
</script>

<template>
    <UiPanel class="w-full" no-padding>
        <div ref="containerRef" class="w-full" :style="{ height: props.height }">
            <v-chart
                v-if="isReady"
                class="h-full w-full"
                :option="props.option"
                :autoresize="props.autoresize"
                :update-options="{ notMerge: true }"
                @mouseover="emit('mouseover', $event)"
                @mouseout="emit('mouseout', $event)"
                @click="emit('click', $event)"
            />
        </div>
    </UiPanel>
</template>
